import { isEmpty } from 'ramda';
import { IS_IOS_MOBILE } from 'reactApp/appHelpers/configHelpers';
import { billingPersonalizationEnabled } from 'reactApp/appHelpers/featuresHelpers/features/billingPersonalization';
import { getBillingPersonalizationTariffsByTab } from 'reactApp/modules/billingPersonalization/billingPersonalization.helpers';
import type {
    BillingPersonalizationState,
    BillingPersonalizationTariffs,
} from 'reactApp/modules/billingPersonalization/billingPersonalization.types';
import type { RootState } from 'reactApp/store';
import type { LoadingState } from 'reactApp/types/commonStates';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { createSelector } from 'reselect';

const getBillingPersonalizationState = (state: RootState): BillingPersonalizationState => state.billingPersonalization;

const getLoadingState = createSelector(
    getBillingPersonalizationState,
    (state: BillingPersonalizationState): LoadingState => ({
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        hasError: state.hasError,
    })
);

// Возвращаем персонализированные линейки тарифов для десктопа. Приводим названия к названием табов.
const getTariffIDsDesktop = createSelector(getBillingPersonalizationState, (state): BillingPersonalizationTariffs => {
    const { web } = state;
    return getBillingPersonalizationTariffsByTab(web);
});

// Возвращаем персонализированные линейки тарифов для мобилки. Приводим названия к названием табов.
const getTariffIDsMobile = createSelector(getBillingPersonalizationState, (state): BillingPersonalizationTariffs => {
    const { iOS, android } = state;

    const data = IS_IOS_MOBILE ? iOS : android;

    return getBillingPersonalizationTariffsByTab(data);
});

const getDefaultPeriod = createSelector(getBillingPersonalizationState, (state: BillingPersonalizationState) => {
    const period = state.web?.default.period;

    if (period === 'year') {
        return ETabsName.year;
    } else if (period === 'month') {
        return ETabsName.month;
    }

    return;
});

const getDefaultProductIds = createSelector(getBillingPersonalizationState, (state): Partial<Record<ETabsName, string | undefined>> => {
    const { web } = state;

    const { minimal, advanced, family, business } = web || {};

    return {
        [ETabsName.basic]: minimal?.default,
        [ETabsName.advanced]: advanced?.default,
        [ETabsName.family]: family?.default,
        [ETabsName.for_work]: business?.default,
    };
});

const getYearTariffIDsDesktop = createSelector(getTariffIDsDesktop, (tariffs) =>
    Object.fromEntries(Object.keys(tariffs).map((key) => [key, tariffs[key].year]))
);

const isBillingPersonalisationEnabled = createSelector(getTariffIDsDesktop, (tariffs) => {
    if (!billingPersonalizationEnabled) {
        return false;
    }

    return Boolean(tariffs) && !isEmpty(tariffs);
});

const isBillingPersonalisationEnabledMobile = createSelector(getTariffIDsMobile, (tariffs) => {
    if (!billingPersonalizationEnabled) {
        return false;
    }

    return Boolean(tariffs) && !isEmpty(tariffs);
});

const getTariffsWebEntryPoints = createSelector(getBillingPersonalizationState, (state): any => {
    const { webEntryPoints } = state;

    if (!webEntryPoints) {
        return null;
    }

    const type = webEntryPoints.default?.type || 'minimal';

    const pattern = webEntryPoints[type]?.default?.match(/W\d+(?=G|_|$)/)?.[0];

    return {
        month: webEntryPoints[type]?.list.month,
        year: webEntryPoints[type]?.list.year,
        recommendedPattern: pattern,
    };
});

const getDefaultPeriodInModal = createSelector(getBillingPersonalizationState, (state: BillingPersonalizationState) => {
    const { webEntryPoints } = state;
    return webEntryPoints?.default.period;
});

export const BillingPersonalizationSelectors = {
    getLoadingState,
    getDefaultPeriod,
    getTariffIDsDesktop,
    getYearTariffIDsDesktop,
    getTariffIDsMobile,
    getDefaultProductIds,
    isBillingPersonalisationEnabled,
    isBillingPersonalisationEnabledMobile,
    getTariffsWebEntryPoints,
    getDefaultPeriodInModal,
};
